import type { EnvironmentId, ServerProvider } from "@cmmd-forge/contracts";

import { isLocalExecutionEnvironmentId } from "../../environments/localExecutionEnvironment";
import { resolveLastKnownInventoryPublishedAt } from "./NewThreadLanding.lastKnownInventorySource";

interface ProviderInventoryConfig {
  readonly providers: ReadonlyArray<ServerProvider>;
}

/**
 * Provider/model inventory for the runtime that will EXECUTE the thread.
 *
 * Returns `null` for "nobody has published an inventory for this target yet",
 * which is a different fact from `[]` ("the runtime answered, and it can run
 * nothing"). Only the target runtime can say what it can execute, and on the
 * cloud surface that answer does not exist until its sprite is connected — the
 * `/new` page routinely renders before any connect has happened.
 *
 * Collapsing both into `[]` is what made the production picker claim every
 * provider was "Coming soon." and print the search-miss sentence "No models
 * found" on a workspace that simply had not been asked yet. Callers that
 * RESOLVE a model still fail closed (`?? []`); callers that RENDER must keep
 * "unknown" distinct from "known to be empty", because only one of them is a
 * setup problem the user can do something about.
 */
export function resolveNewThreadProviderInventory(input: {
  readonly targetEnvironmentId: EnvironmentId | null;
  readonly primaryEnvironmentId: EnvironmentId | null;
  readonly primaryServerConfig: ProviderInventoryConfig | null;
  readonly targetEnvironmentServerConfig: ProviderInventoryConfig | null;
  /**
   * The last catalog the runtime published, reconstituted from the control
   * plane. Breaks the cold-workspace deadlock: a stopped sprite never
   * publishes, and we deliberately will not wake one to fill a dropdown, so
   * without this the picker stayed empty forever and asked the user to send a
   * turn blind in order to discover their own model list.
   */
  readonly lastKnownProviders?: ReadonlyArray<ServerProvider> | null;
  /**
   * This install's CMMD device runtime id, the SECOND name this machine
   * answers to. The Run on chip, the runtime inventory and the saved
   * environment registry all use it, so a draft aimed at this desktop arrives
   * here as `runtime_<uuid>` rather than as the primary Environment id. Without
   * it the resolver hunted for a relay connection to the machine it is running
   * on, found none, and reported the local server as "not started yet".
   *
   * Callers resolve it with `resolveLocalDeviceRuntimeId`, which prefers the
   * `deviceRuntimeId` the connected server publishes over page storage. Storage
   * alone answers for the desktop shell and misreports every other browser on
   * the same machine.
   */
  readonly localDeviceRuntimeId?: string | null;
}): ReadonlyArray<ServerProvider> | null {
  if (input.targetEnvironmentId === null) return null;
  if (
    isLocalExecutionEnvironmentId({
      environmentId: input.targetEnvironmentId,
      primaryEnvironmentId: input.primaryEnvironmentId,
      localDeviceRuntimeId: input.localDeviceRuntimeId ?? null,
    })
  ) {
    return input.primaryServerConfig?.providers ?? null;
  }
  // Live wins, and `[]` is not nullish — so a running runtime that lists
  // nothing keeps its answer instead of being overwritten by a stale catalog
  // of models it just told us it cannot execute. The fallback applies only to
  // the genuinely-unpublished case.
  return input.targetEnvironmentServerConfig?.providers ?? input.lastKnownProviders ?? null;
}

const UNKNOWN_PROVIDER_STATUSES: ReadonlyArray<ServerProvider> = Object.freeze([]);

/**
 * The two readings of one inventory, resolved together so they cannot drift.
 *
 * Model resolution and the model picker want opposite things from an
 * unpublished inventory: resolution must fail closed (no model), while the
 * picker must NOT be handed an empty array it will mistake for a runtime's
 * answer. Deriving both here keeps that single decision in one testable place
 * instead of two `??`s at the call site.
 */
export interface NewThreadProviderInventoryView {
  /** For model resolution. Empty when unknown — fails closed, as before. */
  readonly providerStatuses: ReadonlyArray<ServerProvider>;
  /**
   * Spread onto the picker. `providers` is OMITTED (not `undefined` —
   * `exactOptionalPropertyTypes`) when no runtime has published an inventory,
   * which is how the picker tells "not started yet" from "answered nothing".
   */
  readonly pickerProviderProps: { readonly providers?: ReadonlyArray<ServerProvider> };
  /**
   * When these providers came from a stopped Environment's last-known
   * catalog rather than a live one, the moment that Environment published
   * it. `null` for every other source — live, "not started yet", or the
   * primary environment — so the picker only dates an answer it actually
   * inherited, never a live check's `checkedAt`.
   */
  readonly lastKnownInventoryPublishedAt: string | null;
}

export function resolveNewThreadProviderInventoryView(input: {
  readonly targetEnvironmentId: EnvironmentId | null;
  readonly primaryEnvironmentId: EnvironmentId | null;
  readonly primaryServerConfig: ProviderInventoryConfig | null;
  readonly targetEnvironmentServerConfig: ProviderInventoryConfig | null;
  readonly lastKnownProviders?: ReadonlyArray<ServerProvider> | null;
  readonly lastKnownInventoryPublishedAt?: string | null;
  readonly localDeviceRuntimeId?: string | null;
}): NewThreadProviderInventoryView {
  const inventory = resolveNewThreadProviderInventory(input);
  return {
    providerStatuses: inventory ?? UNKNOWN_PROVIDER_STATUSES,
    pickerProviderProps: inventory === null ? {} : { providers: inventory },
    lastKnownInventoryPublishedAt: resolveLastKnownInventoryPublishedAt({ ...input, inventory }),
  };
}
